import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CheckCircle, XCircle, Clock, Warning, Envelope, Copy,
  ArrowRight, ClipboardText, CaretDown, CaretUp, Lightning, UserSwitch,
  UserPlus, ShieldWarning,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/enterprise/PageHeader';
import { EmptyState } from '@/components/enterprise/EmptyState';
import { formatJST, formatJSTDate } from '@/utils/datetime';

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const YEN = new Intl.NumberFormat('ja-JP');

const STATUS_STYLE = {
  pending: 'bg-amber-100 text-amber-800 border-amber-300',
  approved: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  rejected: 'bg-red-100 text-red-800 border-red-300',
  expired: 'bg-zinc-100 text-zinc-600 border-zinc-300',
};
const STATUS_LABEL = {
  pending: '承認待ち', approved: '承認済', rejected: '却下', expired: '期限切れ',
};

const EVENT_META = {
  created:    { label: '作成',           cls: 'text-zinc-600', Icon: ClipboardText },
  delegated:  { label: '委任',           cls: 'text-blue-600', Icon: UserSwitch },
  escalated:  { label: 'エスカレート',   cls: 'text-orange-600', Icon: Lightning },
  approved:   { label: '承認',           cls: 'text-emerald-600', Icon: CheckCircle },
  rejected:   { label: '却下',           cls: 'text-red-600', Icon: XCircle },
  stalled:    { label: '滞留',           cls: 'text-red-500', Icon: ShieldWarning },
  reminded:   { label: 'リマインド',     cls: 'text-amber-600', Icon: Envelope },
};

/**
 * Approval inbox — every user sees the pending/decided approvals assigned to
 * them (matched by user.id OR email against approver_id / approver_email).
 * Admin also has visibility toggle to see the entire org's approvals.
 *
 * v2 additions:
 *  - `escalation_count` badge + orange highlight on rows that were rotated
 *  - Expand caret reveals a mini timeline of history events
 *  - 「今すぐエスカレート」 button on pending rows
 */
export default function Approvals() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [mineOnly, setMineOnly] = useState(true);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set());

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API_ROOT}/shizai/approvals`, {
      params: { status: filter === 'all' ? undefined : filter, mine_only: mineOnly },
    })
      .then((r) => setRows(r.data || []))
      .catch(() => toast.error('データの取得に失敗しました'))
      .finally(() => setLoading(false));
  }, [filter, mineOnly]);
  useEffect(() => { load(); }, [load]);

  const openApproval = (token) => {
    window.open(`/shizai/approvals/token/${token}`, '_blank');
  };

  const copyLink = async (token) => {
    const link = `${window.location.origin}/shizai/approvals/token/${token}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('承認リンクをコピーしました');
    } catch {
      toast.error('コピーに失敗しました');
    }
  };

  const escalateNow = async (id) => {
    if (!window.confirm('この承認を今すぐエスカレートしますか？（代理承認者または委任先に転送されます）')) return;
    try {
      await axios.post(`${API_ROOT}/shizai/approvals/${id}/escalate-now`, {});
      toast.success('エスカレートしました');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'エスカレートに失敗しました');
    }
  };

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const counts = rows.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-5 page-enter" data-testid="shizai-approvals">
      <PageHeader
        testid="approvals-header"
        title="承認待ち"
        description={`自分に割り当てられた発注承認を審査します（${rows.length}件）`}
        secondary={(
          <>
            <label className="inline-flex items-center gap-2 text-xs text-zinc-700 cursor-pointer">
              <input type="checkbox" checked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)}
                data-testid="approvals-mine-only" />
              自分宛のみ
            </label>
            <Button size="sm" variant="outline" onClick={load} data-testid="approvals-refresh" className="h-10">
              再読込
            </Button>
          </>
        )}
      />

      {/* Filter pills */}
      <div className="flex gap-1.5 flex-wrap">
        {[
          { k: 'pending',  label: `承認待ち (${counts.pending || 0})`, cls: 'bg-amber-500 text-white border-amber-500' },
          { k: 'approved', label: `承認済 (${counts.approved || 0})`, cls: 'bg-emerald-500 text-white border-emerald-500' },
          { k: 'rejected', label: `却下 (${counts.rejected || 0})`, cls: 'bg-red-500 text-white border-red-500' },
          { k: 'all',      label: `すべて`, cls: 'bg-teal-700 text-white border-teal-700' },
        ].map(({ k, label, cls }) => (
          <button key={k} onClick={() => setFilter(k)}
            data-testid={`approvals-filter-${k}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              filter === k ? cls : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="p-12 text-center text-sm text-zinc-400">読み込み中...</div>
      ) : rows.length === 0 ? (
        <EmptyState icon={ClipboardText} title="該当する承認がありません"
          description="承認依頼が届くとここに表示されます" testid="approvals-empty" />
      ) : (
        <div className="space-y-2">
          {rows.map((r) => {
            const escCount = Number(r.escalation_count || 0);
            const isEsc = escCount > 0;
            const isOpen = expanded.has(r.id);
            const history = (r.history || []).slice().sort((a, b) => (a.ts || '').localeCompare(b.ts || ''));
            const isStalled = history.some((h) => h.event === 'stalled');
            return (
              <div key={r.id}
                className={`bg-white rounded-xl border shadow-sm p-4 ${isEsc ? 'border-orange-300 ring-1 ring-orange-100' : 'border-zinc-200'}`}
                data-testid={`approval-row-${r.id}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm font-semibold text-emerald-600">{r.order_no}</span>
                      <Badge variant="outline" className={STATUS_STYLE[r.status] || STATUS_STYLE.pending}>
                        {STATUS_LABEL[r.status] || r.status}
                      </Badge>
                      {isEsc && (
                        <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300 text-[10px]"
                          data-testid={`approval-esc-badge-${r.id}`}>
                          <Lightning size={10} weight="fill" className="mr-0.5 inline" />
                          エスカレート済 ×{escCount}
                        </Badge>
                      )}
                      {isStalled && (
                        <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300 text-[10px]">
                          <ShieldWarning size={10} weight="fill" className="mr-0.5 inline" />
                          滞留（先が未設定）
                        </Badge>
                      )}
                      <span className="text-xs text-zinc-500">{r.rule_name}</span>
                    </div>
                    <div className="mt-1 flex items-baseline gap-3 flex-wrap">
                      <span className="text-lg font-bold text-zinc-800 tabular-nums">¥{YEN.format(r.order_total || 0)}</span>
                      <span className="text-xs text-zinc-500 truncate">
                        {(r.supplier_snapshot || {}).name || '—'}
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-zinc-500 flex items-center gap-3 flex-wrap">
                      <span>依頼者: {r.requested_by_name || '—'}</span>
                      <span>依頼日: {formatJSTDate(r.requested_at)}</span>
                      <span>担当: <b className="text-zinc-700">{r.approver_email}</b></span>
                      {r.status === 'pending' && (
                        <span className="text-amber-700">
                          <Clock size={11} weight="fill" className="inline mr-0.5" />
                          期限: {formatJSTDate(r.expires_at)}
                          {r.idle_days != null && (
                            <span className="ml-1 text-orange-600">・{r.idle_days}日で自動エスカレート</span>
                          )}
                        </span>
                      )}
                      {r.decided_at && (
                        <span>{STATUS_LABEL[r.status]}: {formatJSTDate(r.decided_at)} by {r.decided_by_name}</span>
                      )}
                    </div>
                    {r.comment && (
                      <div className="mt-1.5 text-xs text-zinc-600 bg-zinc-50 rounded p-1.5 border border-zinc-100">
                        💬 {r.comment}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <Button size="sm" onClick={() => openApproval(r.token)}
                      data-testid={`approval-open-${r.id}`}
                      className={`h-9 ${r.status === 'pending' ? 'bg-teal-700 hover:bg-teal-800' : 'bg-zinc-500 hover:bg-zinc-600'}`}>
                      {r.status === 'pending' ? '審査' : '詳細'}
                      <ArrowRight size={13} className="ml-1" weight="bold" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => copyLink(r.token)}
                      data-testid={`approval-copy-${r.id}`}
                      className="h-9 text-xs">
                      <Copy size={12} className="mr-1" />
                      リンク
                    </Button>
                    {r.status === 'pending' && (
                      <Button size="sm" variant="outline"
                        onClick={() => escalateNow(r.id)}
                        data-testid={`approval-escalate-${r.id}`}
                        className="h-9 text-xs border-orange-300 text-orange-700 hover:bg-orange-50">
                        <Lightning size={12} className="mr-1" weight="fill" />
                        エスカレート
                      </Button>
                    )}
                  </div>
                </div>

                {/* Expand toggle */}
                {history.length > 0 && (
                  <button type="button" onClick={() => toggleExpand(r.id)}
                    data-testid={`approval-expand-${r.id}`}
                    className="mt-2.5 inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-zinc-800 transition-colors">
                    {isOpen ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
                    履歴 ({history.length}件)
                  </button>
                )}
                {isOpen && (
                  <div className="mt-2 border-t border-zinc-200 pt-2.5 space-y-1.5"
                    data-testid={`approval-history-${r.id}`}>
                    {history.map((h, idx) => {
                      const meta = EVENT_META[h.event] || { label: h.event, cls: 'text-zinc-500', Icon: ClipboardText };
                      const Icon = meta.Icon;
                      return (
                        <div key={`${h.at || h.date || ''}-${h.event}-${idx}`} className="flex items-start gap-2 text-[11px] pl-1">
                          <Icon size={12} weight="duotone" className={`shrink-0 mt-0.5 ${meta.cls}`} />
                          <div className="flex-1 min-w-0">
                            <span className={`font-semibold ${meta.cls}`}>{meta.label}</span>
                            <span className="text-zinc-400 ml-2 tabular-nums">{formatJST(h.ts)}</span>
                            {(h.from_email || h.to_email) && (
                              <div className="text-zinc-500 mt-0.5">
                                {h.from_email && <span className="font-mono">{h.from_email}</span>}
                                {h.from_email && h.to_email && <ArrowRight size={9} weight="bold" className="inline mx-1" />}
                                {h.to_email && <span className="font-mono text-zinc-700">{h.to_email}</span>}
                              </div>
                            )}
                            {h.note && <div className="text-zinc-500 mt-0.5">{h.note}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Footer note */}
      <div className="text-xs text-zinc-400 mt-4">
        💡 承認/却下は「審査」ボタンをクリック → 承認ページで実行してください。承認リンクはメールでも自動送信されます。<br />
        滞留日数を超えた承認は、まず<b>委任先</b>、次に<b>ルールの代理承認者</b>へ自動でエスカレートします。
      </div>
    </div>
  );
}
